import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  notification,
  Modal,
  Button,
  Input,
  Icon,
  message,
} from "../../../common/UIComponents";
import { LpPreview } from "../../../components/common/linchpin";
import {
  sendAgentTender,
  revokeAgentTender,
  previewAgentTenderDocument,
} from "../../../api/agentTenders";
import { renderAlertMessage } from "../../../helpers/common";
import I18n from "../../../common/I18n";
import "../../../../stylesheets/linchpin/tenders.css";

// Email review step — Figma Tender Email modal + cancel-tender variant.

const PaperPlaneIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path
      d="M1.2 7.35L14.1 1.55c.45-.2.9.25.7.7L8.95 14.8c-.18.4-.75.35-.85-.08L6.8 8.55 1.28 8.2c-.45-.05-.5-.7.0-.85z"
      fill="#1A354D"
    />
  </svg>
);

const formatFileSize = (bytes) => {
  if (bytes == null || bytes === "") return null;
  const n = Number(bytes);
  if (!Number.isFinite(n) || n <= 0) return typeof bytes === "string" ? bytes : null;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const emptyEntry = () => ({ to: [], subject: "", body: "", draft: "" });

const buildEmailsState = (agentEmails) => {
  const initial = {};
  (agentEmails || []).forEach((agent) => {
    const id = agent.agent_id;
    if (id == null) return;
    initial[id] = {
      to: (agent.email || []).filter(Boolean),
      subject: agent.subject || "",
      body: agent.description || "",
      draft: "",
    };
  });
  return initial;
};

const TenderEmailModal = ({ visible, mode, orgId, batchId, agentEmails, revokeOrders, onBack, onDone, resend, orderIds }) => {
  const isRevoke = mode === "revoke";
  // Keep hook count stable across renders (do not add/remove hooks conditionally).
  // Parent remounts this modal via `key` when opening a new tender email stage.
  const [emails, setEmails] = useState(() => buildEmailsState(agentEmails));
  const [hiddenAttachments, setHiddenAttachments] = useState({});
  const [sending, setSending] = useState(false);
  const [preview, setPreview] = useState(null); // { name, url, mime }
  // docKey of the attachment whose PDF is being generated server-side
  const [loadingAttachment, setLoadingAttachment] = useState(null);

  const update = (agentId, patch) =>
    setEmails((prev) => ({
      ...prev,
      [agentId]: { ...(prev[agentId] || emptyEntry()), ...patch },
    }));

  const addRecipient = (agentId) => {
    const entry = emails[agentId] || emptyEntry();
    const raw = (entry.draft || "").trim();
    if (!raw) return;
    const parts = raw.split(/[,;\s]+/).map((p) => p.trim()).filter(Boolean);
    const next = [...entry.to];
    let invalid = false;
    parts.forEach((value) => {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
        invalid = true;
        return;
      }
      if (!next.includes(value)) next.push(value);
    });
    if (invalid) {
      message.error("Enter a valid email address.");
      return;
    }
    update(agentId, { to: next, draft: "" });
  };

  const openAttachment = (agent, doc) => {
    const docKey = `${doc.doc_kind}-${doc.file_name}`;
    if (loadingAttachment) return;
    const params =
      doc.doc_kind === "bol"
        ? { kind: "bol", order_id: doc.order_id, leg_id: doc.leg_id }
        : { kind: "manifest", batch_id: batchId, manifest_key: doc.manifest_key };
    setLoadingAttachment(docKey);
    previewAgentTenderDocument(orgId, params).then((result) => {
      setLoadingAttachment(null);
      if (!result.success) {
        renderAlertMessage(result.errors);
        return;
      }
      const bytes = atob(result.data);
      const buffer = new Uint8Array(bytes.length);
      for (let i = 0; i < bytes.length; i += 1) buffer[i] = bytes.charCodeAt(i);
      const url = URL.createObjectURL(new Blob([buffer], { type: "application/pdf" }));
      setPreview({ name: result.file_name, url, mime: "application/pdf" });
    });
  };

  const hideAttachment = (agentId, docKey) => {
    setHiddenAttachments((prev) => ({
      ...prev,
      [agentId]: [...(prev[agentId] || []), docKey],
    }));
  };

  const agentsPayload = () =>
    agentEmails.map((agent) => {
      const entry = emails[agent.agent_id] || emptyEntry();
      return {
        agent_id: agent.agent_id,
        to: entry.to,
        subject: entry.subject,
        body: entry.body,
      };
    });

  const submit = () => {
    const payload = agentsPayload();
    if (payload.some((agent) => agent.to.length === 0)) {
      message.error("Every agent needs at least one recipient.");
      return;
    }
    setSending(true);
    const request = isRevoke
      ? revokeAgentTender(orgId, revokeOrders, payload, "Revoked from tender modal")
      : sendAgentTender(orgId, batchId, payload, resend, orderIds || []);
    request.then((result) => {
      setSending(false);
      if (!result.success) {
        renderAlertMessage(result.errors);
        return;
      }
      if (isRevoke) {
        // A leg completed in another tab (or by integration) between board load
        // and revoke is skipped server-side — don't claim a blanket success.
        const revokedCount = (result.agent_tender_orders || []).length;
        const skippedCount = (result.skipped || []).length;
        if (!revokedCount && skippedCount) {
          notification.warning({ message: I18n.t("agent_tenders.messages.revoke_all_skipped"), duration: 6 });
        } else if (skippedCount) {
          notification.success({ message: I18n.t("agent_tenders.messages.revoke_some_skipped", { count: skippedCount }), duration: 6 });
        } else {
          notification.success({ message: I18n.t("agent_tenders.messages.revoked_ok"), duration: 6 });
        }
      } else {
        const tnds = (result.tender_numbers || []).join(", ");
        if (resend) {
          notification.success({
            message: tnds
              ? I18n.t("agent_tenders.messages.resent_with_tnd", { tnds })
              : I18n.t("agent_tenders.messages.resent_generic"),
            duration: 6,
          });
        } else {
          notification.success({
            message: tnds
              ? I18n.t("agent_tenders.messages.tendered_with_tnd", { tnds, count: payload.length })
              : I18n.t("agent_tenders.messages.tendered_generic", { count: payload.length }),
            duration: 6,
          });
        }
      }
      onDone(mode);
    });
  };

  const title = isRevoke ? (
    <span><Icon type="close-circle" style={{ color: "#e8862b" }} /> Cancel Tender</span>
  ) : (
    <span><Icon type="mail" /> {resend ? "Resend Tender" : "Tender Email"}</span>
  );

  const intro = isRevoke
    ? "Email tender cancellation to Agent recipients. Nothing is canceled yet."
    : resend
    ? "Emails ready to resend — manifest and BOL attachments included."
    : "Email manifest and BOL attachments to Agent recipients. Nothing is tendered until you send.";

  return (
    <Modal
      visible={visible}
      title={title}
      onCancel={onBack}
      width={720}
      maskClosable={false}
      wrapClassName="lp-v3 atm-modal atm-email-modal"
      footer={
        <React.Fragment>
          <Button className="atm-btn-ghost" onClick={onBack}>
            {isRevoke ? "Never mind" : "Cancel"}
          </Button>
          <span className="atm-footer-spacer" />
          <Button className="atm-btn-secondary" onClick={onBack}>
            Back
          </Button>
          <Button
            type={isRevoke ? "danger" : "primary"}
            loading={sending}
            onClick={submit}
            data-testid="tender-email-send"
            style={{ marginLeft: 8 }}
          >
            {isRevoke ? "Cancel Tender" : resend ? "Resend Tender" : `Send to Agents (${agentEmails.length})`}
          </Button>
        </React.Fragment>
      }
    >
      <div className="atm-email-stack">
        <div className="atm-email-intro">{intro}</div>
        {agentEmails.map((agent, index) => {
          const entry = emails[agent.agent_id] || emptyEntry();
          const hidden = hiddenAttachments[agent.agent_id] || [];
          const attachments = (agent.attachments || []).filter(
            (doc) => !hidden.includes(`${doc.doc_kind}-${doc.file_name}`)
          );
          return (
            <div
              className={`atm-email-card ${isRevoke ? "atm-email-card--revoke" : ""}`}
              key={agent.agent_id}
              data-testid={`tender-email-${agent.agent_id}`}
            >
              <div className={`atm-email-agent ${isRevoke ? "atm-email-agent--revoke" : ""}`}>
                {agentEmails.length > 1
                  ? `Agent ${index + 1} of ${agentEmails.length}: ${agent.agent_name}`
                  : agent.agent_name}
              </div>

              <div className="atm-field-group atm-field-group--to">
                <label className="atm-field-label atm-field-label--md">To</label>
                <Input
                  placeholder="Add an email address, comma separate"
                  value={entry.draft}
                  onChange={(e) => update(agent.agent_id, { draft: e.target.value })}
                  onPressEnter={() => addRecipient(agent.agent_id)}
                  onBlur={() => addRecipient(agent.agent_id)}
                />
                {entry.to.length > 0 && (
                  <div className="atm-recipients">
                    {entry.to.map((address) => (
                      <span className="atm-recipient-chip" key={address}>
                        <span className="atm-recipient-chip-label">{address}</span>
                        <span
                          className="atm-chip-x"
                          role="button"
                          tabIndex={0}
                          onClick={() => update(agent.agent_id, { to: entry.to.filter((a) => a !== address) })}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              update(agent.agent_id, { to: entry.to.filter((a) => a !== address) });
                            }
                          }}
                        >
                          ×
                        </span>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div className="atm-field-group">
                <label className="atm-field-label atm-field-label--md">Subject</label>
                <Input value={entry.subject} onChange={(e) => update(agent.agent_id, { subject: e.target.value })} />
              </div>

              <div className="atm-field-group">
                <label className="atm-field-label atm-field-label--md">Message</label>
                <Input.TextArea
                  rows={4}
                  value={entry.body}
                  onChange={(e) => update(agent.agent_id, { body: e.target.value })}
                />
              </div>

              {!isRevoke && attachments.length > 0 && (
                <div className="atm-field-group">
                  <label className="atm-field-label atm-field-label--md">Attachments</label>
                  <div className="atm-attachment-row">
                    {attachments.map((doc) => {
                      const docKey = `${doc.doc_kind}-${doc.file_name}`;
                      const sizeLabel = formatFileSize(doc.file_size || doc.size);
                      const isLoading = loadingAttachment === docKey;
                      return (
                        <span
                          className={`atm-attachment-chip ${isLoading ? "atm-attachment-chip--loading" : ""}`}
                          key={docKey}
                          onClick={() => openAttachment(agent, doc)}
                          title={isLoading ? "Generating preview…" : "Click to preview"}
                        >
                          {isLoading ? (
                            <Icon type="loading" style={{ fontSize: 13, color: "#586fcc" }} />
                          ) : (
                            <span className="atm-pdf-tag">PDF</span>
                          )}
                          <span className="atm-attachment-meta">
                            <span className="atm-attachment-name">{doc.file_name}</span>
                            {sizeLabel && <span className="atm-attachment-size">{sizeLabel}</span>}
                          </span>
                          <span
                            className="atm-attachment-remove"
                            role="button"
                            tabIndex={0}
                            title="Remove"
                            onClick={(e) => {
                              e.stopPropagation();
                              hideAttachment(agent.agent_id, docKey);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.stopPropagation();
                                hideAttachment(agent.agent_id, docKey);
                              }
                            }}
                          >
                            ×
                          </span>
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      {preview && (
        <LpPreview
          files={[preview]}
          open
          index={0}
          onClose={() => {
            URL.revokeObjectURL(preview.url);
            setPreview(null);
          }}
          onChangeIndex={() => {}}
        />
      )}
    </Modal>
  );
};

TenderEmailModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  mode: PropTypes.oneOf(["tender", "revoke"]).isRequired,
  orgId: PropTypes.string.isRequired,
  batchId: PropTypes.string,
  agentEmails: PropTypes.array,
  revokeOrders: PropTypes.array,
  onBack: PropTypes.func.isRequired,
  onDone: PropTypes.func.isRequired,
  resend: PropTypes.bool,
};

TenderEmailModal.defaultProps = {
  batchId: null,
  agentEmails: [],
  revokeOrders: [],
  resend: false,
};

export default TenderEmailModal;
